import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { WizardStep } from '@/components/WizardShell';
import StudioShell from '@/components/studio/StudioShell';
import StudLivePreview from '@/components/wizard/StudLivePreview';
import WizardSummaryStep from '@/components/wizard/WizardSummaryStep';
import WizardSidebar from '@/pages/KennelWizardV2/WizardSidebar';
import { WizardTopbar } from '@/components/wizard/WizardTopbar';
import { WizardMobileBar } from '@/components/wizard/WizardMobileBar';
import NextStepGuide from '@/components/NextStepGuide';
import { Progress } from '@/components/ui/progress';
import ImageUploader from '@/components/ImageUploader';
import { ModerationInfoBox } from '@/components/ModerationStatus';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Dog, MapPin, Camera, FileText, Crown, Heart, Award,
  GitBranch, Loader2, Check, Upload, Sparkles, LayoutGrid, Image,
  ArrowLeft, ArrowRight, AlertCircle, TrendingUp, CheckCircle2,
  DollarSign, Shield, Building2, Info
} from 'lucide-react';
import { getVoivodeshipFromPostalCode, formatPostalCode, isValidPostalCode } from '@/utils/postalCode';
import { API } from '@/utils/api';
import { firePawConfetti } from '@/utils/confetti';

// ==================== CORE CREATOR IMPORTS ====================
import { validateStep as coreValidateStep, validateAllMust as coreValidateAllMust } from '@/creators/core/validation';
import { computeQualityScore, getStatusConfig } from '@/creators/core/qualityScore';
import { 
  studPremiumSchema, 
  studSteps,
  KENNEL_CLUB_OPTIONS,
  HEALTH_TEST_OPTIONS,
  STUD_FEE_INCLUDES_OPTIONS,
  CHAMPIONSHIP_OPTIONS,
  SHOW_ACHIEVEMENT_OPTIONS,
  WORKING_ACHIEVEMENT_OPTIONS,
  TRAINING_OPTIONS,
  calculateStudQualityScore,
  getStudMissingItems,
  hasAffiliation,
  hasHealthTests,
  hasSufficientFeeIncludes,
  hasSufficientGallery,
  hasContactMethod,
  hasCompleteOffer,
  hasAchievements,
  hasOffspringLinking,
  IMAGE_REQUIREMENTS
} from '@/creators/schemas/stud.premium.schema';

// ==================== BREED DATA ====================

const dogBreeds = [
  'Owczarek Niemiecki Krótkowłosy', 'Owczarek Niemiecki Długowłosy',
  'Labrador Retriever', 'Golden Retriever', 'Buldog Francuski',
  'Beagle', 'Pudel', 'Rottweiler', 'Yorkshire Terrier', 'Bokser', 'Husky Syberyjski',
  'Doberman', 'Shih Tzu', 'Cavalier King Charles Spaniel', 'Border Collie', 
  'Australian Shepherd', 'Cocker Spaniel', 'Maltańczyk', 'Chihuahua', 'Akita',
  'Owczarek Belgijski', 'Amstaff', 'Cane Corso', 'Jack Russell Terrier', 'Mops',
  'Sznaucer', 'Bernardyn', 'Dalmatyńczyk', 'Whippet', 'Shiba Inu', 'Samoyed',
  'Inna rasa'
];

// Reproduktor = wyłącznie pies (usunięto rasy kotów — UI FREEZE: brak opcji „Kot").

const voivodeships = [
  'dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie',
  'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie',
  'podkarpackie', 'podlaskie', 'pomorskie', 'śląskie',
  'świętokrzyskie', 'warmińsko-mazurskie', 'wielkopolskie', 'zachodniopomorskie'
];

const workingBreeds = [
  'Owczarek Niemiecki Krótkowłosy', 'Owczarek Niemiecki Długowłosy',
  'Owczarek Belgijski', 'Rottweiler', 'Doberman', 
  'Amstaff', 'Cane Corso', 'Bokser', 'Husky Syberyjski', 'Akita',
  'Border Collie', 'Australian Shepherd', 'Bernardyn'
];

// Health tests by breed category
const healthTestsByBreed = {
  'large_dogs': [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'spondylosis', label: 'Spondyloza' },
    { id: 'mdr1', label: 'MDR1' },
    { id: 'dm', label: 'DM (mielopatia zwyrodnieniowa)' },
  ],
  'medium_dogs': [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'patella', label: 'Luxacja rzepki' },
  ],
  'small_dogs': [
    { id: 'patella', label: 'Luxacja rzepki' },
    { id: 'pra', label: 'PRA (badanie oczu)' },
    { id: 'heart', label: 'Badanie serca' },
    { id: 'lcp', label: 'Legg-Calvé-Perthes' },
  ],
  'cats': [
    { id: 'felv_fiv', label: 'FeLV/FIV' },
    { id: 'pkd', label: 'PKD' },
    { id: 'hcm', label: 'HCM' },
    { id: 'kidney', label: 'Badanie nerek' },
  ],
  'default': [
    { id: 'hd', label: 'HD (dysplazja bioder)' },
    { id: 'ed', label: 'ED (dysplazja łokci)' },
    { id: 'eyes', label: 'Badanie oczu' },
    { id: 'genetic', label: 'Badanie genetyczne' },
  ]
};

const breedToHealthCategory = {
  'Owczarek Niemiecki Krótkowłosy': 'large_dogs',
  'Owczarek Niemiecki Długowłosy': 'large_dogs',
  'Labrador Retriever': 'large_dogs',
  'Golden Retriever': 'large_dogs',
  'Rottweiler': 'large_dogs',
  'Doberman': 'large_dogs',
  'Yorkshire Terrier': 'small_dogs',
  'Maltańczyk': 'small_dogs',
  'Chihuahua': 'small_dogs',
  'Shih Tzu': 'small_dogs',
  'Mops': 'small_dogs',
};

// Step names for display
const stepNamesMap = studSteps.reduce((acc, s) => {
  acc[s.id] = s.name;
  return acc;
}, {});

// ==================== COMPONENT ====================

export default function StudWizard() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingPedigree, setUploadingPedigree] = useState(false);
  const [publishedHandoff, setPublishedHandoff] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const pedigreeFileRef = useRef(null);
  
  // Available litters for offspring linking
  const [availableLitters, setAvailableLitters] = useState([]);
  // Hodowle użytkownika — do OPCJONALNEGO powiązania reproduktora z hodowlą.
  const [kennels, setKennels] = useState([]);

  const totalSteps = studSteps.length;

  // ==================== FORM STATE ====================
  const [form, setForm] = useState({
    // Step 1: Podstawy
    kennel_id: '',
    name: '',
    species: 'dog',
    breed: '',
    birth_date: '',
    color: '',
    stud_available: true,
    
    // Step 2: Organizacja
    affiliation_type: '',
    affiliation_other_name: '',
    membership_number: '',
    pedigree_name: '',
    pedigree_number: '',
    pedigree_file: '',
    pedigree_link: '',
    sire_name: '',
    sire_titles: '',
    dam_name: '',
    dam_titles: '',
    
    // Step 3: Zdrowie
    selected_health_tests: [],
    health_test_results: {},
    health_tests_text: '',
    
    // Step 4: Oferta
    stud_price: '',
    stud_fee_includes: [],
    stud_conditions: '',
    
    // Step 5: Osiągnięcia
    selected_championships: [],
    selected_show_achievements: [],
    selected_working_achievements: [],
    selected_training: [],
    training_text: '',
    offspring_refs: [],
    offspring_note: '',
    
    // Step 6: Zdjęcia i kontakt
    main_photo: '',
    gallery: [],
    description: '',
    city: '',
    voivodeship: '',
    contact_phone: '',
    contact_whatsapp: '',
    contact_email: '',
    contact_facebook: '',
    contact_instagram: '',
    cta_label: 'Zapytaj o krycie',
    page_layout: 'modern',
  });

  // Terms acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ==================== DATA ADAPTERS ====================
  // Normalize raw API data to form state
  const normalizeStudDraft = (rawData) => {
    return {
      kennel_id: rawData.kennel_id || '',
      name: rawData.name || '',
      species: rawData.species || 'dog',
      breed: rawData.breed || '',
      birth_date: rawData.birth_date || '',
      color: rawData.color || '',
      stud_available: rawData.stud_available !== false,
      
      // Affiliation - handle legacy kennel_club field
      affiliation_type: rawData.affiliation_type || rawData.kennel_club || '',
      affiliation_other_name: rawData.affiliation_other_name || '',
      membership_number: rawData.membership_number || rawData.registration_number || rawData.pedigree_number || '',
      branch_name: rawData.branch_name || '',
      status: rawData.status || 'active',
      pedigree_name: rawData.pedigree_name || '',
      pedigree_number: rawData.pedigree_number || '',
      pedigree_file: rawData.pedigree_file || '',
      pedigree_link: rawData.pedigree_link || '',
      sire_name: rawData.sire_name || '',
      sire_titles: rawData.sire_titles || '',
      dam_name: rawData.dam_name || '',
      dam_titles: rawData.dam_titles || '',
      
      selected_health_tests: rawData.selected_health_tests || [],
      health_test_results: rawData.health_test_results || rawData.health_tests || {},
      health_tests_text: rawData.health_tests_text || '',
      
      stud_price: rawData.stud_price || '',
      stud_fee_includes: rawData.stud_fee_includes || [],
      stud_conditions: rawData.stud_conditions || '',
      
      selected_championships: rawData.selected_championships || [],
      selected_show_achievements: rawData.selected_show_achievements || [],
      selected_working_achievements: rawData.selected_working_achievements || [],
      selected_training: rawData.training || rawData.selected_training || [],
      training_text: rawData.training_text || '',
      offspring_refs: rawData.offspring_refs || [],
      offspring_note: rawData.offspring_note || '',
      
      main_photo: rawData.main_photo || '',
      gallery: rawData.gallery || [],
      description: rawData.description || '',
      city: rawData.city || '',
      voivodeship: rawData.voivodeship || '',
      contact_phone: rawData.contact_phone || '',
      contact_whatsapp: rawData.contact_whatsapp || '',
      contact_email: rawData.contact_email || '',
      contact_facebook: rawData.contact_facebook || '',
      contact_instagram: rawData.contact_instagram || '',
      cta_label: rawData.cta_label || 'Zapytaj o krycie',
      page_layout: rawData.page_layout || 'modern',
    };
  };

  // Denormalize form state to API payload
  const denormalizeStudDraft = (draft) => {
    return {
      name: draft.name,
      species: draft.species,
      gender: 'male', // Always male for studs
      breed: draft.breed,
      birth_date: draft.birth_date,
      color: draft.color,
      stud_available: draft.stud_available,
      
      // Affiliation - save both new and legacy fields
      affiliation_type: draft.affiliation_type,
      affiliation_other_name: draft.affiliation_other_name,
      kennel_club: draft.affiliation_type, // Legacy compat
      // Map affiliation_type to organization_type for ETAP 1 compatibility
      organization_type: (() => {
        const at = draft.affiliation_type;
        if (at === 'zkwp') return 'zkwp_fci';
        if (at === 'fci' || at === 'vdh') return 'fci_other';
        if (at === 'wusv') return 'wush';
        if (at === 'other') return 'other';
        if (at === 'none' || !at) return 'unknown';
        return 'other';
      })(),
      registration_number: draft.membership_number || '',
      branch_name: draft.branch_name || '',
      status: draft.status || 'active',
      membership_number: draft.membership_number,
      pedigree_name: draft.pedigree_name,
      pedigree_number: draft.pedigree_number,
      pedigree_file: draft.pedigree_file,
      pedigree_link: draft.pedigree_link,
      sire_name: draft.sire_name,
      sire_titles: draft.sire_titles,
      dam_name: draft.dam_name,
      dam_titles: draft.dam_titles,
      
      selected_health_tests: draft.selected_health_tests,
      health_tests: draft.health_test_results, // Legacy compat
      health_test_results: draft.health_test_results,
      health_tests_text: draft.health_tests_text || (draft.selected_health_tests || []).join(', '),
      
      stud_price: draft.stud_price,
      stud_fee_includes: draft.stud_fee_includes,
      stud_conditions: draft.stud_conditions,
      
      selected_championships: draft.selected_championships,
      selected_show_achievements: draft.selected_show_achievements,
      selected_working_achievements: draft.selected_working_achievements,
      training: draft.selected_training, // Legacy compat
      selected_training: draft.selected_training,
      training_text: draft.training_text,
      offspring_refs: draft.offspring_refs,
      offspring_note: draft.offspring_note,
      
      main_photo: draft.main_photo,
      gallery: draft.gallery || [],
      description: draft.description,
      city: draft.city,
      voivodeship: draft.voivodeship,
      contact_phone: draft.contact_phone,
      contact_whatsapp: draft.contact_whatsapp,
      contact_email: draft.contact_email,
      contact_facebook: draft.contact_facebook,
      contact_instagram: draft.contact_instagram,
      cta_label: draft.cta_label,
      page_layout: draft.page_layout,
      
      kennel_id: draft.kennel_id || null,
      listing_type: 'stud',
    };
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      if (isEdit && id) {
        fetchStudData();
      }
      fetchAvailableLitters();
      fetchKennels();
    }
  }, [user, authLoading, isEdit, id, navigate]);

  const fetchStudData = async () => {
    setFetchingData(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/studs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm(normalizeStudDraft(res.data));
    } catch (e) {
      console.error('Error fetching stud:', e);
      toast.error('Nie udało się pobrać danych reproduktora');
      navigate('/panel/reproduktor');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchAvailableLitters = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/litters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableLitters(res.data || []);
    } catch (e) {
      console.log('Could not fetch litters for linking');
    }
  };

  const fetchKennels = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/kennels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKennels(res.data || []);
    } catch (e) {
      console.log('Could not fetch kennels for optional linking');
    }
  };

  // ==================== FORM HELPERS ====================
  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const toggleSelection = (field, value) => {
    setForm(prev => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [field]: exists ? current.filter(v => v !== value) : [...current, value]
      };
    });
  };

  // ==================== DERIVED STATE ====================
  const isWorkingBreed = workingBreeds.includes(form.breed);
  const breeds = dogBreeds;
  
  const getHealthTestsForBreed = () => {
    const category = breedToHealthCategory[form.breed] || 'default';
    return healthTestsByBreed[category] || healthTestsByBreed.default;
  };

  // ==================== QUALITY SCORE ====================
  const qualityScoreResult = calculateStudQualityScore(form);
  const { score: qualityScore, suggestions, status: scoreStatus } = qualityScoreResult;
  const missingItems = getStudMissingItems(form);

  // ==================== VALIDATION ====================
  const validateStep = () => {
    const stepErrors = {};

    // SLIM FLOW: tylko absolutne minimum jest wymagane.
    // Step 1 — Pies: name + breed (must).
    if (step === 1) {
      if (!form.name || form.name.trim().length < 2) {
        stepErrors.name = 'Podaj imię reproduktora';
      }
      if (!form.breed) {
        stepErrors.breed = 'Wybierz rasę';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }

    // Step 2 — Organizacja: opcjonalne (tylko walidujemy formę "other" jeśli wybrano).
    if (step === 2) {
      if (form.affiliation_type === 'other' && (!form.affiliation_other_name || form.affiliation_other_name.trim().length < 2)) {
        stepErrors.affiliation_other_name = 'Podaj nazwę organizacji';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }

    // Step 3 — Zdrowie: opcjonalne, można pominąć.
    if (step === 3) {
      setErrors({});
      return true;
    }

    // Step 4 — Oferta: opcjonalne, można pominąć (zostawić puste).
    if (step === 4) {
      setErrors({});
      return true;
    }

    // Step 5 — Osiągnięcia: opcjonalne, zawsze valid.
    if (step === 5) {
      return true;
    }

    // Step 6 — Zdjęcia i kontakt: minimum = main_photo + miasto + 1 kanał kontaktu.
    if (step === 6) {
      if (!form.main_photo) {
        stepErrors.main_photo = 'Dodaj zdjęcie główne';
      }
      if (!form.city) {
        stepErrors.city = 'Podaj miasto';
      }
      if (!hasContactMethod(form)) {
        stepErrors.contact_phone = 'Podaj telefon lub email kontaktowy';
      }
      setErrors(stepErrors);
      return Object.keys(stepErrors).length === 0;
    }

    return true;
  };

  const isStepValid = () => {
    // SLIM FLOW: gating tylko na krok 1 i 6.
    if (step === 1) {
      return form.name && form.name.trim().length >= 2 && form.breed;
    }
    if (step === 6) {
      return form.main_photo && form.city && hasContactMethod(form);
    }
    // Wszystkie inne kroki traktujemy jako opcjonalne — zawsze można iść dalej.
    return true;
  };

  // Validation only on user action (handleNext), not auto

  // ==================== NAVIGATION ====================
  const handleNext = () => {
    const isValid = validateStep();
    if (isValid) {
      setStep(step + 1);
      setErrors({});
    } else {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const fieldEl = document.querySelector(`[data-testid="stud-${firstErrorField}"], [name="${firstErrorField}"]`);
        fieldEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  const goToStep = (targetStep) => {
    setStep(targetStep);
    setErrors({});
  };

  // ==================== FILE UPLOADS ====================
  const handlePedigreeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPedigree(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      updateForm('pedigree_file', res.data.url);
      toast.success('Rodowód przesłany!');
    } catch (e) {
      toast.error('Błąd przesyłania rodowodu');
    } finally {
      setUploadingPedigree(false);
    }
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async () => {
    // Check terms acceptance
    if (!isEdit && !termsAccepted) {
      toast.error('Zaakceptuj warunki publikacji');
      return;
    }

    // SLIM FLOW: blokujemy tylko na absolutnym minimum (imię + rasa + zdjęcie + miasto + kontakt).
    if (!form.name || form.name.trim().length < 2 || !form.breed) {
      toast.error('Uzupełnij imię i rasę');
      setStep(1);
      return;
    }

    if (!form.main_photo) {
      toast.error('Dodaj zdjęcie główne');
      setStep(6);
      return;
    }

    if (!form.city) {
      toast.error('Uzupełnij miasto');
      setStep(6);
      return;
    }

    if (!hasContactMethod(form)) {
      toast.error('Podaj telefon lub email kontaktowy');
      setStep(6);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = denormalizeStudDraft(form);

      let res;
      if (isEdit) {
        res = await axios.put(`${API}/studs/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reproduktor zaktualizowany!');
        navigate(`/reproduktor/${res.data.slug}`);
      } else {
        res = await axios.post(`${API}/studs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Reproduktor opublikowany!');
        firePawConfetti();
        // AUTO-PROGRESSION: pokazujemy handoff zamiast natychmiastowego redirectu.
        setPublishedSlug(res.data.slug);
        setPublishedHandoff(true);
      }
    } catch (e) {
      console.error('Submit error:', e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      
      if (status === 401) {
        toast.error('Sesja wygasła. Zaloguj się ponownie.');
      } else if (status === 422) {
        // Handle validation errors
        let errorMsg = 'Nieprawidłowe dane w formularzu.';
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMsg = detail.map(err => err.msg || 'Błąd walidacji').join(', ');
        }
        toast.error(errorMsg);
      } else {
        toast.error('Błąd zapisu. Spróbuj ponownie.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== LOADING STATE ====================
  if (fetchingData) {
    return (
      <StudioShell mode="workspace" contextLabel="Reproduktor" activeKey="page" sidebarMuted={true} backTo="/panel" backLabel="Wróć do panelu">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[var(--apisai-primary)] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  // ==================== RENDER ====================
  // „Zapisz i wróć później" — brak osobnego systemu szkiców dla studs; wracamy do panelu.
  const handleSaveLater = () => {
    toast('Możesz wrócić i dokończyć w każdej chwili — z panelu „Reproduktory".', { icon: '💡', duration: 3500 });
    navigate('/panel');
  };

  // Checklista podsumowania — spójna ze wszystkimi kreatorami (WizardSummaryStep).
  const studChampCount = (form.selected_championships || []).length + (form.selected_show_achievements || []).length;
  const studDescLen = (form.description || '').length;
  const studSummaryRows = [
    { icon: 'dog', label: 'Imię', ok: !!form.name, value: form.name || 'Uzupełnij imię', hint: 'Imię reproduktora wyświetlane na profilu i w katalogu.', step: 1, required: true },
    { icon: 'paw', label: 'Rasa', ok: !!form.breed, value: form.breed || 'Wybierz rasę', hint: 'Po rasie klienci filtrują katalog — wybierz właściwą.', step: 1, required: true },
    { icon: 'money', label: 'Cena krycia', ok: !!form.stud_price, value: form.stud_price ? `${form.stud_price} zł` : 'Brak ceny', hint: 'Cena krycia to pierwsze, o co pytają właściciele suk.', step: 4, addLabel: 'Uzupełnij' },
    { icon: 'photo', label: 'Zdjęcie główne', ok: !!form.main_photo, value: form.main_photo ? 'Dodane' : 'Brak zdjęcia', hint: 'Profil ze zdjęciem dostaje znacznie więcej zapytań.', step: 6, required: true, addLabel: 'Dodaj' },
    { icon: 'gallery', label: 'Galeria', ok: (form.gallery || []).length > 0, value: (form.gallery || []).length ? `${form.gallery.length} zdjęć` : 'Brak zdjęć', hint: 'Dodaj kilka ujęć z różnych perspektyw.', step: 6, addLabel: 'Dodaj' },
    { icon: 'pedigree', label: 'Rodowód', ok: !!(form.pedigree_number || form.pedigree_name || form.pedigree_file), value: (form.pedigree_number || form.pedigree_name) ? 'Dodany' : 'Brak rodowodu', hint: 'Rodowód i przynależność budują zaufanie.', step: 2, addLabel: 'Uzupełnij' },
    { icon: 'dna', label: 'Badania zdrowotne', ok: (form.selected_health_tests || []).length > 0, value: (form.selected_health_tests || []).length ? `${form.selected_health_tests.length} badań` : 'Brak badań', hint: 'Wyniki badań wyróżniają reproduktora na tle innych.', step: 3, addLabel: 'Dodaj' },
    { icon: 'crown', label: 'Osiągnięcia', ok: studChampCount > 0, value: studChampCount ? 'Dodane' : 'Brak tytułów', hint: 'Tytuły i wystawy podnoszą wartość krycia.', step: 5, addLabel: 'Dodaj' },
    { icon: 'text', label: 'Opis', ok: studDescLen >= 80, value: studDescLen ? (studDescLen < 80 ? `Krótki opis (${studDescLen} znaków)` : 'Dodany') : 'Brak opisu', hint: 'Opowiedz o charakterze, temperamencie i osiągnięciach.', step: 6, addLabel: 'Uzupełnij' },
    { icon: 'map', label: 'Lokalizacja', ok: !!form.city, value: form.city || 'Brak miasta', hint: 'Lokalizacja pomaga klientom z okolicy znaleźć reproduktora.', step: 6, required: true, addLabel: 'Uzupełnij' },
    { icon: 'phone', label: 'Kontakt', ok: hasContactMethod(form), value: hasContactMethod(form) ? 'Dodany' : 'Brak kontaktu', hint: 'Podaj telefon lub e-mail, żeby klienci mogli się odezwać.', step: 6, required: true, addLabel: 'Uzupełnij' },
  ];

  // ==================== WIZARD CHROME (spójne z KennelWizardV2 / LitterWizard / DogWizardNew) ====================
  const studProgressPct = Math.round((step / totalSteps) * 100);
  const studCompletedSteps = Array.from({ length: Math.max(0, step - 1) }, (_, i) => i + 1);
  const sidebarTitle = (
    <>
      Budujemy prezentację <span className="text-orange-500">reproduktora</span>
    </>
  );
  const studTopbarSlot = (
    <WizardTopbar
      prefix="stud-wizard"
      step={step}
      totalSteps={totalSteps}
      stepName={stepNamesMap[step]}
      progressPct={studProgressPct}
      isLastStep={step === totalSteps}
      submitting={loading}
      nextDisabled={loading || (step === totalSteps && !isEdit && !termsAccepted)}
      publishLabel={isEdit ? 'Zapisz zmiany' : 'Opublikuj reproduktora'}
      onSaveExit={handleSaveLater}
      onNext={step === totalSteps ? handleSubmit : handleNext}
      viewHref={publishedSlug ? `/reproduktor/${publishedSlug}` : undefined}
    />
  );

  return (
    <StudioShell mode="workspace" contextLabel={isEdit ? 'Edytuj reproduktora' : 'Dodaj reproduktora'} topbarSlot={studTopbarSlot} activeKey="page" sidebarMuted={true} backTo="/panel" backLabel="Wróć do panelu">
      <NextStepGuide
        open={publishedHandoff}
        currentStep={4}
        userName={user?.name}
        entityType="kennel"
        creationType="stud"
        viewLink={publishedSlug ? { href: `/reproduktor/${publishedSlug}`, label: `apisai.pl/reproduktor/${publishedSlug}` } : null}
        onClose={() => {
          setPublishedHandoff(false);
          if (publishedSlug) navigate(`/reproduktor/${publishedSlug}`);
        }}
      />
      <div data-testid="stud-wizard-v2">
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">

          {/* LEWA KOLUMNA: KROKI — ukryta na kroku Podsumowanie (pełna szerokość) */}
          {step !== totalSteps && (
          <WizardSidebar
            steps={studSteps}
            currentStep={step}
            completedSteps={studCompletedSteps}
            onStepClick={goToStep}
            title={sidebarTitle}
          />
          )}

          {/* ŚRODEK: FORMULARZ (na kroku Podsumowanie — treść od lewej, bez ramki karty; podgląd zostaje po prawej) */}
          <section className="flex-1 min-w-0 w-full" data-testid="stud-wizard-form-panel">
            <div className={step === totalSteps ? '' : 'apisai-soft-card overflow-hidden'}>
              <AnimatePresence mode="wait">
            {/* STEP 1: Podstawy */}
            {step === 1 && (
              <WizardStep
                key="step1"
                icon={Dog}
                title="Podstawowe informacje"
                subtitle="Imię, rasa i status dostępności"
              >
                <div className="space-y-4">
                  {/* Powiązanie z hodowlą — OPCJONALNE (reproduktor może być samodzielną stroną psa) */}
                  <div data-testid="stud-kennel-link">
                    <Label>Powiązanie z hodowlą (opcjonalnie)</Label>
                    {kennels.length > 0 ? (
                      <>
                        <Select
                          value={form.kennel_id || '__none__'}
                          onValueChange={(v) => updateForm('kennel_id', v === '__none__' ? '' : v)}
                        >
                          <SelectTrigger data-testid="stud-kennel-select">
                            <SelectValue placeholder="Wybierz hodowlę lub zostaw bez powiązania" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Bez hodowli — samodzielna strona psa</SelectItem>
                            {kennels.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-500 mt-1">
                          Możesz powiązać reproduktora ze stroną hodowli teraz albo później. Bez powiązania działa jako samodzielna oferta krycia.
                        </p>
                      </>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="stud-no-kennel-hint">
                        Nie masz jeszcze hodowli — reproduktor będzie samodzielną stroną psa. Możesz powiązać go z hodowlą później.
                      </div>
                    )}
                  </div>

                  {/* Gatunek: reproduktor jest zawsze psem — selektor usunięty (UI FREEZE: brak opcji „Kot"). */}

                  {/* Name */}
                  <div>
                    <Label>Imię rodowe *</Label>
                    <Input
                      data-testid="stud-name"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="np. Champion Rex z Doliny Słońca"
                      className={`h-14 text-lg ${errors.name ? 'border-red-500' : ''}`}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Breed */}
                  <div>
                    <Label>Rasa *</Label>
                    <Select value={form.breed} onValueChange={(v) => updateForm('breed', v)}>
                      <SelectTrigger className={`h-14 ${errors.breed ? 'border-red-500' : ''}`} data-testid="stud-breed">
                        <SelectValue placeholder="Wybierz rasę" />
                      </SelectTrigger>
                      <SelectContent>
                        {breeds.map(breed => (
                          <SelectItem key={breed} value={breed}>{breed}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.breed && <p className="text-red-500 text-sm mt-1">{errors.breed}</p>}
                  </div>

                  {/* Birth date & Color */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Data urodzenia</Label>
                      <Input
                        type="date"
                        value={form.birth_date}
                        onChange={(e) => updateForm('birth_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Umaszczenie</Label>
                      <Input
                        value={form.color}
                        onChange={(e) => updateForm('color', e.target.value)}
                        placeholder="np. Czarno-podpalany"
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <Label>Status dostępności *</Label>
                    <Select 
                      value={form.stud_available ? 'true' : 'false'} 
                      onValueChange={(v) => updateForm('stud_available', v === 'true')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">✅ Dostępny do krycia</SelectItem>
                        <SelectItem value="false">⏸️ Niedostępny / Wstrzymany</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 2: Organizacja */}
            {step === 2 && (
              <WizardStep
                key="step2"
                icon={Shield}
                title="Organizacja i przynależność"
                subtitle="Związek kynologiczny i rodowód"
              >
                <div className="space-y-4">
                  {/* Affiliation Type */}
                  <div>
                    <Label>Przynależność do związku *</Label>
                    <Select 
                      value={form.affiliation_type} 
                      onValueChange={(v) => updateForm('affiliation_type', v)}
                    >
                      <SelectTrigger className={errors.affiliation_type ? 'border-red-500' : ''} data-testid="stud-affiliation_type">
                        <SelectValue placeholder="Wybierz organizację" />
                      </SelectTrigger>
                      <SelectContent>
                        {KENNEL_CLUB_OPTIONS.map(opt => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.affiliation_type && <p className="text-red-500 text-sm mt-1">{errors.affiliation_type}</p>}
                  </div>

                  {/* Custom organization name (when 'other' selected) */}
                  {form.affiliation_type === 'other' && (
                    <div>
                      <Label>Nazwa organizacji *</Label>
                      <Input
                        data-testid="stud-affiliation_other_name"
                        value={form.affiliation_other_name}
                        onChange={(e) => updateForm('affiliation_other_name', e.target.value)}
                        placeholder="Wpisz nazwę organizacji"
                        className={errors.affiliation_other_name ? 'border-red-500' : ''}
                      />
                      {errors.affiliation_other_name && <p className="text-red-500 text-sm mt-1">{errors.affiliation_other_name}</p>}
                    </div>
                  )}

                  {/* Membership number */}
                  <div>
                    <Label>Numer rejestracyjny / rodowodu</Label>
                    <Input
                      value={form.membership_number}
                      onChange={(e) => updateForm('membership_number', e.target.value)}
                      placeholder="np. PKR.I-12345"
                    />
                  </div>

                  {/* Pedigree name */}
                  <div>
                    <Label>Pełna nazwa rodowodowa</Label>
                    <Input
                      value={form.pedigree_name}
                      onChange={(e) => updateForm('pedigree_name', e.target.value)}
                      placeholder="np. Champion Rex z Doliny Słońca FCI"
                    />
                  </div>

                  {/* Pedigree file/link */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Plik rodowodu (PDF/JPG)</Label>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handlePedigreeUpload}
                        ref={pedigreeFileRef}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => pedigreeFileRef.current?.click()}
                        disabled={uploadingPedigree}
                        className="w-full"
                      >
                        {uploadingPedigree ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {form.pedigree_file ? 'Zmień plik' : 'Prześlij'}
                      </Button>
                      {form.pedigree_file && (
                        <p className="text-xs text-green-600 mt-1">✓ Plik przesłany</p>
                      )}
                    </div>
                    <div>
                      <Label>Link do rodowodu online</Label>
                      <Input
                        value={form.pedigree_link}
                        onChange={(e) => updateForm('pedigree_link', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  {/* Parents */}
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-3">Rodzice (opcjonalnie)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Imię ojca</Label>
                        <Input
                          value={form.sire_name}
                          onChange={(e) => updateForm('sire_name', e.target.value)}
                          placeholder="np. Champion Max"
                        />
                      </div>
                      <div>
                        <Label>Tytuły ojca</Label>
                        <Input
                          value={form.sire_titles}
                          onChange={(e) => updateForm('sire_titles', e.target.value)}
                          placeholder="np. Ch.PL, C.I.B."
                        />
                      </div>
                      <div>
                        <Label>Imię matki</Label>
                        <Input
                          value={form.dam_name}
                          onChange={(e) => updateForm('dam_name', e.target.value)}
                          placeholder="np. Luna"
                        />
                      </div>
                      <div>
                        <Label>Tytuły matki</Label>
                        <Input
                          value={form.dam_titles}
                          onChange={(e) => updateForm('dam_titles', e.target.value)}
                          placeholder="np. Ch.PL"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 3: Zdrowie */}
            {step === 3 && (
              <WizardStep
                key="step3"
                icon={Heart}
                title="Badania zdrowotne"
                subtitle="Wybierz min. 1 badanie"
              >
                <div className="space-y-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      Badania zdrowotne *
                      <span className="text-xs text-zinc-500">(min. 1)</span>
                    </Label>
                    <p className="text-xs text-zinc-500 mb-3">Wybrane badania zwiększają zaufanie kupujących</p>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {getHealthTestsForBreed().map(test => (
                        <label 
                          key={test.id} 
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            form.selected_health_tests.includes(test.id)
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-zinc-200 dark:border-gray-700 hover:border-zinc-300'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_health_tests.includes(test.id)}
                            onCheckedChange={() => toggleSelection('selected_health_tests', test.id)}
                          />
                          <span className="text-sm">{test.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.selected_health_tests && (
                      <p className="text-red-500 text-sm mt-2">{errors.selected_health_tests}</p>
                    )}
                  </div>

                  {/* Health test results */}
                  {form.selected_health_tests.length > 0 && (
                    <div className="pt-4 border-t">
                      <Label>Wyniki badań (opcjonalnie)</Label>
                      <p className="text-xs text-zinc-500 mb-3">Podaj wyniki dla wybranych badań</p>
                      <div className="grid grid-cols-2 gap-3">
                        {form.selected_health_tests.map(testId => {
                          const test = getHealthTestsForBreed().find(t => t.id === testId);
                          return (
                            <div key={testId}>
                              <Label className="text-xs">{test?.label || testId}</Label>
                              <Input
                                value={form.health_test_results[testId] || ''}
                                onChange={(e) => updateForm('health_test_results', {
                                  ...form.health_test_results,
                                  [testId]: e.target.value
                                })}
                                placeholder="np. A, 0/0, N/N"
                                className="h-9"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Additional health info */}
                  <div>
                    <Label>Dodatkowe informacje o badaniach</Label>
                    <Textarea
                      value={form.health_tests_text}
                      onChange={(e) => updateForm('health_tests_text', e.target.value)}
                      placeholder="Inne badania, certyfikaty, uwagi..."
                      rows={3}
                    />
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 4: Oferta */}
            {step === 4 && (
              <WizardStep
                key="step4"
                icon={DollarSign}
                title="Oferta i cena"
                subtitle="Cena krycia i co zawiera"
              >
                <div className="space-y-4">
                  {/* Price */}
                  <div>
                    <Label>Cena krycia *</Label>
                    <Input
                      data-testid="stud-stud_price"
                      value={form.stud_price}
                      onChange={(e) => updateForm('stud_price', e.target.value)}
                      placeholder="np. 2000 zł"
                      className={`h-14 text-lg ${errors.stud_price ? 'border-red-500' : ''}`}
                    />
                    {errors.stud_price && <p className="text-red-500 text-sm mt-1">{errors.stud_price}</p>}
                  </div>

                  {/* Fee includes */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Co zawiera cena *
                      <span className="text-xs text-zinc-500">(min. 3)</span>
                    </Label>
                    <p className="text-xs text-zinc-500 mb-3">Wybrane: {(form.stud_fee_includes || []).length}/3 min.</p>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {STUD_FEE_INCLUDES_OPTIONS.map(option => (
                        <label 
                          key={option.id} 
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            form.stud_fee_includes.includes(option.id)
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-zinc-200 dark:border-gray-700 hover:border-zinc-300'
                          }`}
                        >
                          <Checkbox
                            checked={form.stud_fee_includes.includes(option.id)}
                            onCheckedChange={() => toggleSelection('stud_fee_includes', option.id)}
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.stud_fee_includes && (
                      <p className="text-red-500 text-sm mt-2">{errors.stud_fee_includes}</p>
                    )}
                  </div>

                  {/* Conditions */}
                  <div>
                    <Label>Warunki krycia (opcjonalnie)</Label>
                    <Textarea
                      value={form.stud_conditions}
                      onChange={(e) => updateForm('stud_conditions', e.target.value)}
                      placeholder="np. wymagane badania suczki, umowa, warunki powtórki..."
                      rows={3}
                    />
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 5: Osiągnięcia */}
            {step === 5 && (
              <WizardStep
                key="step5"
                icon={Crown}
                title="Osiągnięcia i potomstwo"
                subtitle="Tytuły, szkolenia (opcjonalnie)"
              >
                <div className="space-y-6">
                  {/* Championships */}
                  <div>
                    <Label>Championaty</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {CHAMPIONSHIP_OPTIONS.map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            form.selected_championships.includes(opt.id)
                              ? 'border-[#F47A3B] bg-[#F47A3B]/5'
                              : 'border-zinc-200 dark:border-gray-700'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_championships.includes(opt.id)}
                            onCheckedChange={() => toggleSelection('selected_championships', opt.id)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Show achievements */}
                  <div>
                    <Label>Osiągnięcia wystawowe</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {SHOW_ACHIEVEMENT_OPTIONS.map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            form.selected_show_achievements.includes(opt.id)
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-zinc-200 dark:border-gray-700'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_show_achievements.includes(opt.id)}
                            onCheckedChange={() => toggleSelection('selected_show_achievements', opt.id)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Working achievements (only for working breeds) */}
                  {isWorkingBreed && (
                    <div>
                      <Label>Osiągnięcia użytkowe</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {WORKING_ACHIEVEMENT_OPTIONS.map(opt => (
                          <label 
                            key={opt.id} 
                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                              form.selected_working_achievements.includes(opt.id)
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                : 'border-zinc-200 dark:border-gray-700'
                            }`}
                          >
                            <Checkbox
                              checked={form.selected_working_achievements.includes(opt.id)}
                              onCheckedChange={() => toggleSelection('selected_working_achievements', opt.id)}
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Training */}
                  <div>
                    <Label>Szkolenia i egzaminy</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {(isWorkingBreed ? [...TRAINING_OPTIONS.working, ...TRAINING_OPTIONS.general] : TRAINING_OPTIONS.general).map(opt => (
                        <label 
                          key={opt.id} 
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                            form.selected_training.includes(opt.id)
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-zinc-200 dark:border-gray-700'
                          }`}
                        >
                          <Checkbox
                            checked={form.selected_training.includes(opt.id)}
                            onCheckedChange={() => toggleSelection('selected_training', opt.id)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Training text */}
                  <div>
                    <Label>Inne szkolenia / osiągnięcia</Label>
                    <Textarea
                      value={form.training_text}
                      onChange={(e) => updateForm('training_text', e.target.value)}
                      placeholder="Opisz inne osiągnięcia..."
                      rows={2}
                    />
                  </div>

                  {/* Offspring linking */}
                  <div className="pt-4 border-t">
                    <Label>Potomstwo</Label>
                    <p className="text-xs text-zinc-500 mb-3">Powiąż mioty z tego reproduktora (zwiększa zaufanie)</p>
                    
                    {availableLitters.length > 0 ? (
                      <div className="space-y-2">
                        {availableLitters.map(litter => (
                          <label 
                            key={litter.id} 
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              form.offspring_refs.includes(litter.id)
                                ? 'border-[#F47A3B] bg-[#F47A3B]/10'
                                : 'border-zinc-200 dark:border-gray-700'
                            }`}
                          >
                            <Checkbox
                              checked={form.offspring_refs.includes(litter.id)}
                              onCheckedChange={() => toggleSelection('offspring_refs', litter.id)}
                            />
                            <div>
                              <span className="font-medium">{litter.name || 'Miot'}</span>
                              {litter.birth_date && (
                                <span className="text-xs text-zinc-500 ml-2">({litter.birth_date})</span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-500 italic">Brak miotów do powiązania</p>
                    )}
                    
                    <div className="mt-3">
                      <Label className="text-sm">Potomstwo poza bazą</Label>
                      <Textarea
                        value={form.offspring_note}
                        onChange={(e) => updateForm('offspring_note', e.target.value)}
                        placeholder="Opisz potomstwo jeśli nie ma go w bazie..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </WizardStep>
            )}

            {/* STEP 6: Zdjęcia i kontakt */}
            {step === 6 && (
              <WizardStep
                key="step6"
                icon={Camera}
                title="Zdjęcia i kontakt"
                subtitle="Galeria i dane kontaktowe"
              >
                <div className="space-y-6">
                  {/* Combined image uploader for cover + gallery */}
                  <ImageUploader
                    coverImage={form.main_photo}
                    gallery={form.gallery || []}
                    onCoverChange={(url) => updateForm('main_photo', url)}
                    onGalleryChange={(urls) => updateForm('gallery', urls)}
                    maxGalleryImages={20}
                  />
                  {errors.main_photo && <p className="text-red-500 text-sm mt-1">{errors.main_photo}</p>}
                  {errors.gallery && <p className="text-red-500 text-sm mt-1">{errors.gallery}</p>}

                  {/* Description */}
                  <div>
                    <Label>Opis reproduktora</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => updateForm('description', e.target.value)}
                      placeholder="Opisz charakter, temperament, osiągnięcia..."
                      rows={4}
                    />
                  </div>

                  {/* Location */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Miasto *</Label>
                      <Input
                        data-testid="stud-city"
                        value={form.city}
                        onChange={(e) => updateForm('city', e.target.value)}
                        placeholder="np. Warszawa"
                        className={errors.city ? 'border-red-500' : ''}
                      />
                      {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <Label>Województwo *</Label>
                      <Select value={form.voivodeship} onValueChange={(v) => updateForm('voivodeship', v)}>
                        <SelectTrigger className={errors.voivodeship ? 'border-red-500' : ''} data-testid="stud-voivodeship">
                          <SelectValue placeholder="Wybierz" />
                        </SelectTrigger>
                        <SelectContent>
                          {voivodeships.map(v => (
                            <SelectItem key={v} value={v}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.voivodeship && <p className="text-red-500 text-sm mt-1">{errors.voivodeship}</p>}
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="pt-4 border-t">
                    <Label className="flex items-center gap-2">
                      Kontakt *
                      <span className="text-xs text-zinc-500">(min. telefon lub email)</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-xs">Telefon</Label>
                        <Input
                          data-testid="stud-contact_phone"
                          value={form.contact_phone}
                          onChange={(e) => updateForm('contact_phone', e.target.value)}
                          placeholder="+48 123 456 789"
                          className={errors.contact_phone ? 'border-red-500' : ''}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                          value={form.contact_email}
                          onChange={(e) => updateForm('contact_email', e.target.value)}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">WhatsApp</Label>
                        <Input
                          value={form.contact_whatsapp}
                          onChange={(e) => updateForm('contact_whatsapp', e.target.value)}
                          placeholder="+48 123 456 789"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Facebook</Label>
                        <Input
                          value={form.contact_facebook}
                          onChange={(e) => updateForm('contact_facebook', e.target.value)}
                          placeholder="https://facebook.com/..."
                        />
                      </div>
                    </div>
                    {errors.contact_phone && (
                      <p className="text-red-500 text-sm mt-2">{errors.contact_phone}</p>
                    )}
                  </div>

                  {/* CTA Label */}
                  <div>
                    <Label>Tekst przycisku kontaktu</Label>
                    <Input
                      value={form.cta_label}
                      onChange={(e) => updateForm('cta_label', e.target.value)}
                      placeholder="Zapytaj o krycie"
                    />
                  </div>

                  {/* Layout */}
                  <div>
                    <Label>Styl strony</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => updateForm('page_layout', 'modern')}
                        className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                          form.page_layout === 'modern' 
                            ? 'border-[#F47A3B] bg-[#F47A3B]/10' 
                            : 'border-zinc-200 dark:border-gray-700'
                        }`}
                      >
                        <LayoutGrid className="w-6 h-6" />
                        <span className="text-sm font-medium">Nowoczesny</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateForm('page_layout', 'classic')}
                        className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                          form.page_layout === 'classic' 
                            ? 'border-[#F47A3B] bg-[#F47A3B]/10' 
                            : 'border-zinc-200 dark:border-gray-700'
                        }`}
                      >
                        <FileText className="w-6 h-6" />
                        <span className="text-sm font-medium">Klasyczny</span>
                      </button>
                    </div>
                  </div>

                </div>
              </WizardStep>
            )}

            {/* STEP 7: Podsumowanie — wspólny WizardSummaryStep (spójny ze wszystkimi kreatorami) */}
            {step === 7 && (
              <div key="step-summary">
                <WizardSummaryStep
                  headerTitle="Profil reproduktora jest gotowy do publikacji"
                  headerSubtitle="Po publikacji reproduktor pojawi się w katalogu APISAI i otrzyma własną stronę z formularzem zapytań."
                  pageCardTitle="Strona reproduktora"
                  checklist={studSummaryRows}
                  goToStep={goToStep}
                  termsAccepted={termsAccepted}
                  setTermsAccepted={setTermsAccepted}
                  termsTestId="stud-terms-checkbox"
                  dataAccurateTestId="stud-data-accurate"
                  onPublish={handleSubmit}
                  onSaveLater={handleSaveLater}
                  submitting={loading}
                  isEdit={isEdit}
                  publishLabel="Opublikuj reproduktora"
                  testId="stud-wizard-summary"
                />
              </div>
            )}
          </AnimatePresence>
            </div>

            {/* Krok końcowy (Podsumowanie) ma własne przyciski — nawigacja przez WizardTopbar + kroki / WizardMobileBar */}
          </section>

          {/* PRAWA KOLUMNA: PODGLĄD NA ŻYWO — widoczny też na kroku Podsumowanie (układ „Birch Haus": treść od lewej + podgląd po prawej) */}
          <section className="hidden lg:block w-full lg:w-[420px] xl:w-[460px] flex-shrink-0" data-testid="stud-preview-pane">
            <div className="lg:sticky lg:top-24">
              <StudLivePreview form={form} />
            </div>
          </section>
        </div>
      </main>

      {/* MOBILE: wspólny WizardMobileBar (pływający podgląd + dolny pasek akcji + arkusz) */}
      <WizardMobileBar
        prefix="stud-wizard"
        step={step}
        totalSteps={totalSteps}
        isLastStep={step === totalSteps}
        submitting={loading}
        nextDisabled={loading || (step === totalSteps && !isEdit && !termsAccepted)}
        publishLabel={isEdit ? 'Zapisz zmiany' : 'Opublikuj reproduktora'}
        onBack={handleBack}
        onSaveExit={handleSaveLater}
        onNext={step === totalSteps ? handleSubmit : handleNext}
        previewNode={<StudLivePreview form={form} />}
      />
      </div>
    </StudioShell>
  );
}
